import React from 'react'
import { Container, Theme } from '@mui/material'
import { Trans } from 'react-i18next'
import { BlogArticleQueryResult } from '@graphql/queries/__generated__/blog.generated'
import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography'
import FormattedDate from '@components/FormattedDate'
import { Link } from 'react-router-dom'
import { getWholesaleBlogUrl } from '../../../urls'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { getLightboxUiElements } from '../../../utils/imagesLightbox'

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    backgroundColor: '#fff',
    padding: '10px 20px 20px 20px',
    '& h4': {
      color: '#282828',
      fontWeight: 600,
      marginBottom: 6,
      padding: '10px 18.5px',
    },
    '& h5': {
      color: '#282828',
      padding: '0 10px',
    },
  },
  mainImg: {
    textAlign: 'center',
    position: 'relative',
    padding: '28%',
    overflow: 'hidden',
    cursor: 'pointer',
    '& img': {
      maxWidth: '100%',
      position: 'absolute',
      left: 0,
      top: 0,
    },
  },
  additionalInfo: {
    display: 'flex',
    margin: '10px 0',
    color: '#656969',
    '& span': {
      padding: '0 5px',
      '&:not(:last-of-type)': {
        borderRight: '1px solid #656969',
      },
    },
  },
  body: {
    backgroundColor: '#fff',
    padding: '50px',
    '& img': {
      maxWidth: '100%',
    },
  },
}))

interface Props {
  articleQueryResult: BlogArticleQueryResult
  blogSlug: string
}

const Page: React.FC<Props> = ({ articleQueryResult, blogSlug }) => {
  const classes = useStyles()

  const article = articleQueryResult.data?.blogArticle
  if (!article) return null

  const authorName = article.authorName
  return (
    <div style={{ backgroundColor: '#f5f6f8' }}>
      <div className='md:py-20'>
        <Container maxWidth='lg' disableGutters>
          <>
            <div className={classes.header}>
              <Typography variant='h4'>
                <span>{article.title}</span>
              </Typography>
              <Typography variant='body1'>
                <span>{article.annotation}</span>
              </Typography>
            </div>
            <div className={classes.additionalInfo}>
              <span>
                <FormattedDate value={article.dateAdded} />
              </span>
              <span>
                <Trans i18nKey='articleByAuthorWithLink'>By {{ authorName }}</Trans>
              </span>
              {article.categories.map(node => (
                <span key={node.id}>
                  <Link to={getWholesaleBlogUrl(blogSlug)}>{node.name}</Link>
                </span>
              ))}
            </div>
            <div className={classes.mainImg}>
              <Gallery uiElements={getLightboxUiElements()} withDownloadButton>
                <Item
                  original={article.bigThumbnail}
                  thumbnail={article.bigThumbnailWebp}
                  width='1600'
                  height='900'
                  alt={article.title}
                >
                  {({ ref, open }) => (
                    <img
                      className='cursor-pointer'
                      ref={ref as React.MutableRefObject<HTMLImageElement>}
                      src={article.bigThumbnailWebp}
                      alt={article.title}
                      onClick={open}
                    />
                  )}
                </Item>
              </Gallery>
            </div>
          </>
        </Container>
        <div className='my-20' />
        <Container maxWidth='lg' disableGutters>
          <div className={classes.body}>
            <span
              dangerouslySetInnerHTML={{
                __html: article.body,
              }}
            />
          </div>
        </Container>
      </div>
    </div>
  )
}

export default Page
